import { jwtDecode } from "jwt-decode";
import {
  ActiveMonitoringRequestBody,
  BpMonitoringRequestSchema,
  BpReadingRaw,
  BpReadingsResponseSchema,
} from "../components/Monitoring/types";

const getToken = () => {
  const token = localStorage.getItem("jwt");
  if (!token) throw new Error("cannot find token—is user authenticated?");
  return token;
};

// v2 API base (same var requestHelpers.js uses). Old code appended "/v2"
// to rails' host, which 404'd locally since rails doesn't path-route it.
const baseUrl = import.meta.env.VITE_API_V2_URL;

const checkToken = (token: string): void => {
  const tokenData = jwtDecode(token) as { ptid?: string };
  if (
    ("ptid" in tokenData === false || !tokenData.ptid) &&
    import.meta.env.DEV === false
  ) {
    throw new Error("ptid is undefined! does the patient have an athena ID?");
  }
};

export const getIsActiveMonitoringRequest = async (): Promise<
  { active: false } | ({ active: true } & ActiveMonitoringRequestBody)
> => {
  const token = getToken();
  try {
    checkToken(token);
  } catch {
    console.warn(
      "patient does not have an athena ID—will not check if there is an active BP monitoring request",
    );
    return { active: false };
  }
  const url = `${baseUrl}/blood-pressure-monitoring-request`;
  const opts = {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const res = await fetch(url, opts);
  if (res.status !== 200)
    throw new Error(`request failed with status ${res.status}`);
  const jsonPayload = await res.json();

  const payload = BpMonitoringRequestSchema.parse(jsonPayload);
  if (!payload.activeMonitoringRequest) return { active: false };
  // we can rely on the backend to return null if the monitoring request has lapsed
  else return { active: true, ...payload.activeMonitoringRequest };
};

export const getReadings = async () => {
  const token = getToken();
  checkToken(token);
  const url = `${baseUrl}/blood-pressure-readings`;
  const opts = {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const res = await fetch(url, opts);
  if (res.status !== 200)
    throw new Error(`request failed with status ${res.status}`);
  const data = await res.json();
  const {
    activeMonitoringRequest: { readings },
  } = BpReadingsResponseSchema.parse(data);
  return readings.sort(
    ({ timeOfReading: t1 }, { timeOfReading: t2 }) =>
      t1.getTime() - t2.getTime(),
  );
};

export const postReading = async (reading: BpReadingRaw) => {
  const token = getToken();
  checkToken(token);
  const url = `${baseUrl}/blood-pressure-reading`;
  const opts: RequestInit = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(reading),
  };
  const res = await fetch(url, opts);
  if (res.status !== 200)
    throw new Error(`request failed with status ${res.status}`);
  if (res.ok) return;
  else throw new Error(`${res.status} (${res.statusText})`);
};
